import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, FolderKanban, FileText, Wrench, BookOpen, LogOut, Menu, X, UserCog, KeyRound } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = 'https://crm.ashbournesystems.ca/api';

export default function Sidebar({ onLogout, role, userName, token }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwLoading, setPwLoading] = useState(false);

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/clients', label: 'Clients', icon: Users },
    { path: '/projects', label: 'Projects', icon: FolderKanban },
    { path: '/invoices', label: 'Invoices', icon: FileText },
    { path: '/maintenance', label: 'Maintenance', icon: Wrench },
    { path: '/documents', label: 'Documents', icon: BookOpen },
    ...(role === 'owner' || role === 'admin' ? [{ path: '/users', label: 'Users', icon: UserCog }] : []),
  ];

  const handleNav = (path) => { navigate(path); setMobileOpen(false); };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (pwForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setPwLoading(true);
    try {
      await axios.put(`${API}/auth/password`, {
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Password updated');
      setShowPasswordModal(false);
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error updating password');
    } finally {
      setPwLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '0.65rem', background: '#0e0804',
    border: '1px solid #2a1a0e', color: '#F5ECD7', fontSize: '0.82rem',
    outline: 'none', boxSizing: 'border-box', marginBottom: '0.75rem',
    fontFamily: 'sans-serif',
  };

  const labelStyle = {
    display: 'block', fontSize: '0.6rem', color: '#8a6840',
    letterSpacing: '0.12em', marginBottom: '4px',
  };

  const sidebarContent = (
    <>
      <div style={{ padding: '1.5rem', borderBottom: '1px solid #2a1a0e' }}>
        <div style={{ fontFamily: 'Georgia, serif', fontSize: '1.2rem', fontWeight: 'bold', color: '#F5ECD7' }}>
          Ashbourne<span style={{ color: '#B85A22' }}>.</span>Systems
        </div>
        <div style={{ fontSize: '0.65rem', color: '#8a6840', letterSpacing: '0.15em', marginTop: '2px' }}>
          CLIENT MANAGEMENT
        </div>
      </div>

      <nav style={{ flex: 1, padding: '1rem 0' }}>
        {navItems.map(({ path, label, icon: Icon }) => {
          const active = location.pathname === path;
          return (
            <button key={path} onClick={() => handleNav(path)} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem',
              padding: '0.875rem 1.5rem', background: active ? 'rgba(184,90,34,0.15)' : 'transparent',
              border: 'none', borderLeft: active ? '3px solid #B85A22' : '3px solid transparent',
              color: active ? '#F5ECD7' : '#8a6840', cursor: 'pointer', fontSize: '0.9rem',
              transition: 'all 0.2s', textAlign: 'left'
            }}>
              <Icon size={18} />
              {label}
            </button>
          );
        })}
      </nav>

      <div style={{ padding: '1rem', borderTop: '1px solid #2a1a0e' }}>
        {userName && (
          <div style={{ marginBottom: '0.75rem', padding: '0.75rem', background: 'rgba(184,90,34,0.08)', border: '1px solid #2a1a0e' }}>
            <div style={{ fontSize: '0.6rem', color: '#8a6840', letterSpacing: '0.1em', marginBottom: '4px' }}>SIGNED IN AS</div>
            <div style={{ fontSize: '0.85rem', color: '#F5ECD7', fontWeight: 'bold' }}>{userName}</div>
            <div style={{ fontSize: '0.62rem', color: '#B85A22', marginBottom: '0.6rem' }}>{role?.toUpperCase()}</div>
            <button onClick={() => setShowPasswordModal(true)} style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              background: 'transparent', border: '1px solid #2a1a0e',
              color: '#8a6840', padding: '0.35rem 0.6rem', cursor: 'pointer',
              fontSize: '0.65rem', letterSpacing: '0.05em', width: '100%',
            }}>
              <KeyRound size={11} /> Change Password
            </button>
          </div>
        )}
        <button onClick={onLogout} style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '0.75rem 1rem', background: 'transparent', border: '1px solid #2a1a0e',
          color: '#8a6840', cursor: 'pointer', fontSize: '0.8rem',
        }}>
          <LogOut size={15} />
          Sign Out
        </button>
      </div>
    </>
  );

  return (
    <>
      <div style={{
        width: '240px', height: '100vh', background: '#1a0f08',
        borderRight: '1px solid #2a1a0e', position: 'fixed', left: 0, top: 0,
        display: 'flex', flexDirection: 'column', zIndex: 100,
      }} className="desktop-sidebar">
        {sidebarContent}
      </div>

      <div style={{
        display: 'none', position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
        background: '#1a0f08', borderBottom: '1px solid #2a1a0e',
        padding: '1rem 1.5rem', alignItems: 'center', justifyContent: 'space-between',
      }} className="mobile-topbar">
        <div style={{ fontFamily: 'Georgia, serif', fontSize: '1.1rem', fontWeight: 'bold', color: '#F5ECD7' }}>
          Ashbourne<span style={{ color: '#B85A22' }}>.</span>Systems
        </div>
        <button onClick={() => setMobileOpen(!mobileOpen)} style={{
          background: 'transparent', border: 'none', color: '#F5ECD7', cursor: 'pointer', padding: '4px'
        }}>
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 199, background: 'rgba(0,0,0,0.5)' }}
          onClick={() => setMobileOpen(false)}>
          <div style={{
            width: '280px', height: '100vh', background: '#1a0f08',
            borderRight: '1px solid #2a1a0e', display: 'flex', flexDirection: 'column',
          }} onClick={e => e.stopPropagation()}>
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setShowPasswordModal(false)}>
          <div style={{
            background: '#1a0f08', border: '1px solid #2a1a0e', borderTop: '2px solid #B85A22',
            padding: '2rem', width: '100%', maxWidth: '380px', margin: '1rem',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ color: '#F5ECD7', fontSize: '1rem', margin: 0, fontFamily: 'Georgia, serif' }}>Change Password</h3>
              <button onClick={() => setShowPasswordModal(false)} style={{ background: 'none', border: 'none', color: '#8a6840', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handlePasswordChange}>
              <label style={labelStyle}>CURRENT PASSWORD</label>
              <input type="password" style={inputStyle} value={pwForm.currentPassword}
                onChange={e => setPwForm({...pwForm, currentPassword: e.target.value})} required />
              <label style={labelStyle}>NEW PASSWORD</label>
              <input type="password" style={inputStyle} value={pwForm.newPassword}
                onChange={e => setPwForm({...pwForm, newPassword: e.target.value})} required />
              <label style={labelStyle}>CONFIRM NEW PASSWORD</label>
              <input type="password" style={{...inputStyle, marginBottom: '1.25rem'}} value={pwForm.confirmPassword}
                onChange={e => setPwForm({...pwForm, confirmPassword: e.target.value})} required />
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button type="submit" disabled={pwLoading} style={{
                  flex: 1, padding: '0.75rem', background: '#B85A22', border: 'none',
                  color: '#F5ECD7', cursor: 'pointer', fontSize: '0.75rem',
                  letterSpacing: '0.1em', fontWeight: 'bold',
                }}>
                  {pwLoading ? 'UPDATING...' : 'UPDATE PASSWORD'}
                </button>
                <button type="button" onClick={() => setShowPasswordModal(false)} style={{
                  padding: '0.75rem 1rem', background: 'transparent',
                  border: '1px solid #2a1a0e', color: '#8a6840', cursor: 'pointer', fontSize: '0.75rem',
                }}>
                  CANCEL
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-sidebar { display: none !important; }
          .mobile-topbar { display: flex !important; }
        }
      `}</style>
    </>
  );
}